import React, { useRef, useEffect } from 'react';
import { FormGroup, Button } from 'src/components/ui';
import SignatureCanvas from 'react-signature-canvas';
import BrowserImageManipulation from 'browser-image-manipulation';
import Icon from 'src/components/Icon';

// Plain numbers are pixels; anything else (e.g. the default '100%') is already a CSS length.
const toCssSize = size => (/^\d+(\.\d+)?$/.test(`${size}`) ? `${size}px` : size);

function InteractiveDrawing(props) {
  const {
    formData,
    title,
    altTitle,
    background,
    classNames,
    penColor,
    $id,
    required,
    ref,
    clearSignature,
    onChange,
    width,
    height,
    isDisabled,
  } = useInteractiveDrawingHooks(props);

  useEffect(() => {
    if (!ref.current) return;
    if (formData) {
      ref.current.fromDataURL(formData);
    } else if (background) {
      ref.current.fromDataURL(background);
    }
  }, [formData]);

  return (
    <FormGroup disabled={isDisabled}>
      <div>
        <label className="control-label" htmlFor={$id}>
          {title}
          {required ? <span className="required">*</span> : null}
        </label>
        <div
          className={`${classNames || ''} form-control interactive-drawing`}
          style={{
            width: toCssSize(width),
            height: toCssSize(height),
          }}
        >
          {isDisabled ? (
            <img
              src={formData}
              alt={altTitle}
              style={{
                width: toCssSize(width),
                height: toCssSize(height),
              }}
            />
          ) : (
            <SignatureCanvas
              ref={ref}
              penColor={penColor}
              canvasProps={{ className: 'sign signature-pad writable' }}
              disabled={isDisabled}
              required={required}
              onEnd={onChange}
              value={formData}
              backgroundColor="white"
            />
          )}
          <div className="button-bar tr">
            {!isDisabled ? (
              <Button onClick={clearSignature} disabled={isDisabled} title="Clear Signature">
                <Icon icon="Trash" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </FormGroup>
  );
}

function useInteractiveDrawingHooks(props) {
  const {
    formData,
    disabled,
    schema: { title, altTitle = 'firma', background },
    uiSchema: { 'ui:classNames': classNames, 'ui:readonly': readonly, size = ['100%', 300], penColor = 'red' },
    fieldPathId: { $id, path },
    required,
    onChange: propOnChange,
  } = props;

  const ref = useRef();

  const [width, height] = size;
  const isDisabled = readonly || disabled;

  return {
    formData,
    title,
    altTitle,
    background,
    classNames,
    penColor,
    $id,
    required,
    ref,
    clearSignature() {
      ref.current.clear();
      ref.current.fromDataURL(background);
    },
    async onChange() {
      const data = ref.current.toDataURL('image/png');
      const blob = await fetch(data).then(res => {
        return res.blob();
      });
      new BrowserImageManipulation()
        .loadBlob(blob)
        .resize(width, height)
        .saveAsImage()
        .then(base64 => {
          propOnChange(base64, path);
        })
        .catch(e => console.error(e.toString()));
    },
    attachSignature(data) {
      propOnChange(data, path);
    },
    width,
    height,
    isDisabled,
  };
}

export default InteractiveDrawing;
